import type { ReactNode } from 'react';
import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

// `token()` is required: Text does not resolve raw token paths, and
// there is no `critical-text` token — `critical-icon` is the standard
// critical red (same as LocationPickerFlyout).
const CRITICAL_TEXT_COLOR = token('color.status.critical-icon');

const ASTERISK_GAP = 'dimensions.spacing.50';

export interface RequiredLabelProps {
  readonly children: ReactNode;
  /** Renders the trailing red asterisk. */
  readonly isRequired: boolean;
  /** Colors the whole label red — pair with the control's `error` prop. */
  readonly hasError: boolean;
  readonly fontWeight?: 'bold' | 'regular';
}

/**
 * Site-wide required-field label: `[label] [red *]`. The asterisk is
 * visual only — keep `aria-required` on the paired control.
 */
export function RequiredLabel({
  children,
  isRequired,
  hasError,
  fontWeight = 'bold',
}: RequiredLabelProps): JSX.Element {
  const labelColor = hasError ? CRITICAL_TEXT_COLOR : undefined;
  return (
    <Row alignItems="center" gridGap={ASTERISK_GAP}>
      <Text fontWeight={fontWeight} {...(labelColor ? { color: labelColor } : {})}>
        {children}
      </Text>
      {isRequired && (
        <Text
          fontWeight={fontWeight}
          color={CRITICAL_TEXT_COLOR}
          aria-hidden="true"
          data-testid="required-asterisk"
        >
          *
        </Text>
      )}
    </Row>
  );
}
